/**
 * Copyright (c) 2010-2020 California CAD Solutions, Inc. Licensed subject
 * to an End-User License Agreement included in a separate file named EULA.txt
 * that is distributed with this file.
 */
import { AsyncPipe } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltip } from '@angular/material/tooltip';
import { StorageDirectUrl, StorageFile } from '@app/types/FileDownload';
import { ShowError } from '@core/state/notification/notification.actions';
import { LayoutAlignDirective, LayoutDirective } from '@ngbracket/ngx-layout/flex';
import { Store } from '@ngxs/store';
import { DataFormService } from '@reports/services/data-form.service';
import { LoadDataForm } from '@reports/state/data-form.actions';
import { DataFieldValueModel, DataFormDefinition, DataFormValueModel, LoadDataFormPayload } from '@reports/state/data-form.model';
import { DataFormState } from '@reports/state/data-form.state';
import { DashGisUtil } from '@shared/dash-gis-util';
import { LoggerService } from '@shared/services/logger.service';
import { StorageService } from '@shared/services/storage.service';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, concatMap, map, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { FileDeleteConfirmComponent } from './file-delete-confirm.component';

@Component({
    selector: 'dash-gis-file-download',
    templateUrl: './file-download.component.html',
    encapsulation: ViewEncapsulation.Emulated,
    styleUrls: [ './file-download.component.scss' ],
    imports: [ LayoutDirective, LayoutAlignDirective, MatIconButton, MatTooltip, MatIcon, AsyncPipe ]
})
export class FileDownloadComponent implements OnInit, OnChanges {
    @Output() changed = new EventEmitter<void>();
    @Output() loaded = new EventEmitter<void>();

    @Input() dataForm: string;
    @Input() fileHash: string;
    @Input() key: string;
    @Input() thumbnailWidth?: number;

    fileType$: Observable<string>;
    icon$: Observable<string>;
    thumbnailUrl$: Observable<string>;

    private _definition$: Observable<(string) => DataFormDefinition> = inject(Store).select(DataFormState.definition);
    private _value$: Observable<(string) => DataFormValueModel> = inject(Store).select(DataFormState.value);

    constructor(
        private logger: LoggerService,
        private storageService: StorageService,
        private dataFormService: DataFormService,
        private dialog: MatDialog,
        private snackBar: MatSnackBar,
        private store: Store
    ) {}

    ngOnInit() {}

    ngOnChanges(changes: SimpleChanges) {
        if (changes.fileHash && changes.fileHash.previousValue !== changes.fileHash.currentValue) {
            this.fileType$ = this.getFileMimeType$().pipe(
                map((mimeType) => {
                    const reResult = /^([-.\w]+)\/([-.\w]+)/.exec(mimeType);
                    if (reResult && reResult.length === 3) {
                        return reResult[1].toLowerCase() === 'image' ? 'image' : reResult[2];
                    }
                    return mimeType;
                })
            );
            this.icon$ = this.getFileMimeType$().pipe(map(this.storageService.mimeTypeToIcon));
            this.thumbnailUrl$ = this.fileType$.pipe(
                mergeMap((fileType) =>
                    fileType === 'image'
                        ? this.storageService.getImageThumbnailUrl(this.fileHash, this.thumbnailWidth)
                        : EMPTY
                )
            );
        }
    }

    imageLoaded() {
        this.loaded.emit();
    }

    download() {
        this.storageService
            .retrieveFile(this.fileHash)
            .pipe(
                take(1),
                switchMap((storageObject) => {
                    if (storageObject['content'] !== undefined) {
                        return this.storageService.handleBlob(storageObject as StorageFile);
                    } else if (storageObject['url'] !== undefined) {
                        return this.storageService.handleUrl(storageObject as StorageDirectUrl);
                    } else {
                        this.store.dispatch(new ShowError('Invalid response when retrieving file.'));
                        return EMPTY;
                    }
                })
            )
            .subscribe();
    }

    confirmRemove() {
        this.dialog
            .open(FileDeleteConfirmComponent)
            .afterClosed()
            .subscribe((result: boolean) => {
                if (result) {
                    this.remove();
                }
            });
    }

    remove() {
        const dataFormPayload: LoadDataFormPayload = {
            name: this.dataForm,
            keys: [ this.key.toString() ]
        };
        this.store
            .dispatch(new LoadDataForm(dataFormPayload))
            .pipe(
                concatMap(() => this.formValue$),
                concatMap((dataFormModel: DataFormValueModel) => {
                    const newModel = DashGisUtil.clone(dataFormModel);
                    newModel.fields.forEach((field: DataFieldValueModel) => {
                        if (field.name.toLowerCase() === 'deleted') {
                            field.value = 1;
                            field.dirty = true;
                        }
                    });
                    return this.dataFormService.saveData(newModel);
                }),
                concatMap(() => this.storageService.removeFile(this.fileHash)),
                tap((result) => {
                    if (result !== true) {
                        throw new Error('Error removing file.');
                    }
                }),
                catchError((err) => {
                    this.logger.error(err);
                    this.snackBar.open(err);
                    return of(false);
                }),
                take(1)
            )
            .subscribe(() => this.changed.emit());
    }

    private getFileMimeType$() {
        return this.storageService.getFileMimeType(this.fileHash);
    }

    private getThumbnail(): Observable<string> {
        if (!this.fileHash) {
            return of('');
        }
        return this.storageService.getFileMimeType(this.fileHash).pipe(
            concatMap((mimeType: string): Observable<string> => {
                if (this.isImageType(mimeType)) {
                    return this.storageService.getImageThumbnailUrl(this.fileHash, this.thumbnailWidth);
                } else {
                    return of('');
                }
            })
        );
    }

    private isImageType(mimeType: string): boolean {
        return mimeType.split('/')[0] === 'image';
    }

    public get formDefinition$() {
        return this._definition$.pipe(map((fn) => fn(this.dataForm)));
    }

    public get formValue$() {
        return this._value$.pipe(map((fn) => fn(this.dataForm)));
    }
}
